// @flow

import * as React from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
  CircularProgress,
  Grid,
  Box,
  useTheme,
} from '@material-ui/core'
import MuiDialogActions from '@material-ui/core/DialogActions'
import { createStyles, makeStyles, withStyles } from '@material-ui/core/styles'
const useStyles = makeStyles((theme) =>
  createStyles({
    formControlLabel: { fontSize: '1.5rem' },
    wrapper: {
      margin: theme.spacing(1),
      position: 'relative',
    },
    buttonProgress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    },
    fillScreen: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        minHeight: '100%',
        position: 'fixed',
        margin: 0,
      },
    },
  })
)

const StyledDialogActionsCheckbox = withStyles((theme) => ({
  root: {
    margin: '0 16px',
    padding: theme.spacing(1),
    justifyContent: 'left',
  },
}))(MuiDialogActions)

const StyledDialogActionsAccept = withStyles((theme) => ({
  root: {
    margin: '0 16px 16px',
    padding: theme.spacing(1),
    justifyContent: 'left',
  },
}))(MuiDialogActions)

type Props = {
  isContinueButtonDisabled: boolean,
  onAcceptCallback: () => Promise<boolean>,
  goNextPageCallback: () => void,
  iframeUrl: string,
  pdfUrl: string,
}

function TermsAndConditions({
  onAcceptCallback,
  goNextPageCallback,
  iframeUrl,
  pdfUrl,
  isContinueButtonDisabled,
}: Props): React.Element<'div'> {
  const [isOpen, setIsOpen] = React.useState(false)
  const [accepted, setAccepted] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const theme = useTheme()
  function openModal() {
    // For small screens which user had to scroll down to click on Continue button,
    // scroll to the top of the page after
    window.scrollTo(0, 0)

    setIsOpen(true)
  }

  function handleCheck(event: SyntheticInputEvent<HTMLInputElement>) {
    setAccepted(event.target.checked)
  }

  function handleClose() {
    setIsOpen(false)
  }

  async function handleAccept() {
    setIsLoading(true)
    const success = await onAcceptCallback()
    setIsLoading(false)
    setIsOpen(false)

    if (success) {
      goNextPageCallback()
    }
  }

  const descriptionElementRef = React.useRef(null)
  React.useEffect(() => {
    if (isOpen) {
      const { current: descriptionElement } = descriptionElementRef
      if (descriptionElement !== null) {
        descriptionElement.focus()
      }
    }
  }, [isOpen])

  const classes = useStyles()
  return (
    <div>
      <Button
        disabled={isContinueButtonDisabled}
        onClick={() => openModal()}
        color="primary"
        variant="contained"
        fullWidth
      >
        Continue
      </Button>
      <Dialog
        classes={{ paper: classes.fillScreen }}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="scroll-dialog-title" disableTypography>
          <Typography variant="h4">Please review and accept</Typography>
        </DialogTitle>
        <DialogContent style={{ overflow: 'hidden' }}>
          <Typography variant="h5">
            <a href={pdfUrl} rel="noopener noreferrer" target="_blank">
              Download as PDF
            </a>
          </Typography>
          <DialogContentText
            id="scroll-dialog-description"
            ref={descriptionElementRef}
            tabIndex={-1}
          >
            <iframe
              title="Terms And Conditions"
              src={iframeUrl}
              width="100%"
              height="600px"
              sandbox=""
            />
          </DialogContentText>
        </DialogContent>
        <StyledDialogActionsCheckbox>
          <FormControlLabel
            control={<Checkbox checked={accepted} onChange={handleCheck} color="primary" />}
            label={
              <Typography classes={{ root: classes.formControlLabel }}>
                By checking this box, I confirm I have read and agreed to Dr.Bill's Terms and
                Conditions.
              </Typography>
            }
          />
        </StyledDialogActionsCheckbox>
        <StyledDialogActionsAccept>
          <Grid container item spacing={2} style={theme.rightAlignItem}>
            <Grid
              component={Box}
              item
              style={{ padding: '0px' }}
              xs={12}
              sm="auto"
              display={{ xs: 'block', sm: 'none' }}
            >
              <div className={classes.wrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!accepted || isLoading}
                  onClick={handleAccept}
                  fullWidth
                >
                  Create Account
                </Button>
                {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
              </div>
            </Grid>
            <Grid item style={{ padding: '0px' }} xs={12} sm="auto">
              <div className={classes.wrapper}>
                <Button onClick={handleClose} color="primary" fullWidth>
                  Cancel
                </Button>
              </div>
            </Grid>
            <Grid
              component={Box}
              item
              style={{ padding: '0px' }}
              xs={12}
              sm="auto"
              display={{ xs: 'none', sm: 'block' }}
            >
              <div className={classes.wrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!accepted || isLoading}
                  onClick={handleAccept}
                  fullWidth
                >
                  Create account
                </Button>
                {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
              </div>
            </Grid>
          </Grid>
        </StyledDialogActionsAccept>
      </Dialog>
    </div>
  )
}

export default TermsAndConditions
